import React from "react";
import PropTypes from "prop-types";
import FALLBACK_PLANS from "../../constants/plans";
import { isValidUpgrade, isValidDowngrade } from "../../utils/planUtils";
import { PlanCard, UpgradeModal } from "../billing";
import CurrentPlanCard from "./CurrentPlanCard";
import UsageHistoryTable from "./UsageHistoryTable";

/**
 * Complete billing section including current plan, plan selection, and usage history.
 */
const BillingSection = ({
  plans,
  planOrder,
  currentPlanId,
  planStatus,
  planExpiresAt,
  invoiceHistory,
  loadingInvoices,
  upgradingPlan,
  openingPortal,
  confirmingUpgrade,
  salesEmail,
  toast,
  onUpgradeClick,
  onUpgradeConfirm,
  onUpgradeCancel,
  onManageSubscription,
}) => {
  const displayPlans = plans || FALLBACK_PLANS;
  const currentPlan = displayPlans.find((p) => p.id === currentPlanId);
  const canUpgradeTo = (planId) =>
    isValidUpgrade(currentPlanId, planId, planOrder);
  const isPlanDowngrade = (planId) =>
    isValidDowngrade(currentPlanId, planId, planOrder);

  return (
    <section className="settings-section">
      <h2 className="settings-section-title">Billing</h2>

      {/* Current Plan */}
      <CurrentPlanCard
        currentPlan={currentPlan}
        currentPlanId={currentPlanId}
        planStatus={planStatus}
        planExpiresAt={planExpiresAt}
        openingPortal={openingPortal}
        onManageSubscription={onManageSubscription}
      />

      {/* Plan Selection */}
      <div
        className={`billing-plans-grid ${upgradingPlan ? "billing-plans-loading" : ""}`}
      >
        {displayPlans.map((plan) => {
          const isCurrent = plan.id === currentPlanId;
          const isUpgrade = canUpgradeTo(plan.id);
          const isDowngrade = isPlanDowngrade(plan.id);
          const isUpgrading = upgradingPlan === plan.id;
          const isOtherUpgrading = upgradingPlan && upgradingPlan !== plan.id;

          return (
            <PlanCard
              key={plan.id}
              plan={plan}
              isCurrent={isCurrent}
              isUpgrade={isUpgrade}
              isDowngrade={isDowngrade}
              isUpgrading={isUpgrading}
              isOtherUpgrading={isOtherUpgrading}
              openingPortal={openingPortal}
              salesEmail={salesEmail}
              onUpgradeClick={onUpgradeClick}
              onManageSubscription={onManageSubscription}
              toast={toast}
            />
          );
        })}
      </div>

      {/* Usage History */}
      <UsageHistoryTable
        invoiceHistory={invoiceHistory}
        loading={loadingInvoices}
      />

      {/* Upgrade Confirmation Modal */}
      <UpgradeModal
        plan={confirmingUpgrade}
        onConfirm={onUpgradeConfirm}
        onCancel={onUpgradeCancel}
      />
    </section>
  );
};

BillingSection.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      price: PropTypes.number,
      features: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
  planOrder: PropTypes.arrayOf(PropTypes.string),
  currentPlanId: PropTypes.string,
  planStatus: PropTypes.string,
  planExpiresAt: PropTypes.string,
  invoiceHistory: PropTypes.array,
  loadingInvoices: PropTypes.bool,
  upgradingPlan: PropTypes.string,
  openingPortal: PropTypes.bool,
  confirmingUpgrade: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    price: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.string),
  }),
  salesEmail: PropTypes.string,
  toast: PropTypes.shape({
    success: PropTypes.func,
    error: PropTypes.func,
    info: PropTypes.func,
  }).isRequired,
  onUpgradeClick: PropTypes.func.isRequired,
  onUpgradeConfirm: PropTypes.func.isRequired,
  onUpgradeCancel: PropTypes.func.isRequired,
  onManageSubscription: PropTypes.func.isRequired,
};

// defaultProps retained — component uses non-destructured props pattern
BillingSection.defaultProps = {
  currentPlanId: "free",
  invoiceHistory: [],
};

export default BillingSection;
